import { linkToRecord } from '@/hooks/use-linked-record';
import type { Notification } from '../types/notification.types';

/** What a notification says, from its `denormalizedPayload` (a JSON text); empty when it is not one. */
export interface NotificationContent {
  title: string;
  description: string;
  /** The record it is about (blocks/workflows lib/notices.js notifyBody); '' for older notices and Blocks' own. */
  entityId: string;
}

export const notificationContent = (
  denormalizedPayload: string | undefined
): NotificationContent => {
  let parsed: Record<string, unknown> = {};
  try {
    const value: unknown = JSON.parse(denormalizedPayload ?? '');
    if (value && typeof value === 'object') parsed = value as Record<string, unknown>;
  } catch {
    // Not ours: shown without text rather than breaking the whole list.
  }
  const text = (key: string) => (typeof parsed[key] === 'string' ? (parsed[key] as string) : '');
  // Blocks' own notices (e.g. "Key Translation Completed") write their text in lower case.
  return {
    title: text('Title') || text('title'),
    description: text('Description') || text('description'),
    entityId: text('EntityId'),
  };
};

/**
 * The page a notification opens, from the `responseKey` / `responseValue` the workflows send
 * (blocks/workflows: lib/notices.js notificationRequest and each module's notices). The value says who
 * was told: a request handed to its reviewers opens the manager page, a decision on the person's own
 * request opens their own page. With the record's id (`entityId`) the page opens that record
 * (useLinkedRecord); a break or an escalation names the employee, whose attendance opens. `null` for
 * anything else (e.g. Blocks' own notices): nothing to open.
 */
export const notificationTarget = (
  payload: Pick<Notification['payload'], 'responseKey' | 'responseValue'> | undefined,
  entityId = ''
): string | null => {
  const key = payload?.responseKey ?? '';
  const value = payload?.responseValue ?? '';
  const employeeAttendance = entityId
    ? `/hr/manage-attendance/${entityId}`
    : '/hr/manage-attendance';
  switch (key) {
    case 'LeaveRequest':
      // Submitted / withdrawn go to the department heads; reviewed / cancelled to the employee.
      return linkToRecord(
        value === 'LeaveRequestSubmitted' || value === 'LeaveRequestWithdrawn'
          ? '/hr/leave-manager'
          : '/hr/my-leave',
        entityId
      );
    case 'ToilRequest':
      return linkToRecord(value === 'Requested' ? '/hr/toil-manager' : '/hr/my-toil', entityId);
    case 'AttendanceCorrection':
      return linkToRecord(
        value === 'Requested' ? '/hr/manage-attendance' : '/hr/my-attendance',
        entityId,
        'corrections'
      );
    case 'AttendanceBreak':
      return employeeAttendance;
    case 'AttendanceReminder':
      return value === 'Escalation' ? employeeAttendance : '/hr/my-attendance';
    default:
      // The page's first tab lists the application: a reviewer's requests (their own included), else
      // the person's own.
      return SERVICE_DESK_PATHS[key] ? linkToRecord(SERVICE_DESK_PATHS[key], entityId) : null;
  }
};

/** Service desk notices name their application schema; one page per application type. */
const SERVICE_DESK_PATHS: Record<string, string> = {
  NocApplication: '/hr/service-desk/noc',
  ByodApplication: '/hr/service-desk/byod',
  BusinessCardApplication: '/hr/service-desk/business-card',
  IdCardApplication: '/hr/service-desk/id-card',
  MetLifeApplication: '/hr/service-desk/insurance',
};
